import { useEffect, useMemo, useState } from "preact/hooks";
import type { JSX } from "preact";
import {
  deleteStory,
  getStory,
  getStoryCast,
  listCharacters,
  saveStory,
  setStoryCast,
  setStoryPublished,
  type CharacterRow,
} from "@/lib/api";
import {
  newChoiceId,
  type SceneChoice,
  type SceneKind,
  type StoryModel,
  type StoryScene,
} from "@/story/model";
import { compileStory, type CompileReport } from "@/story/compiler";
import { starterModel, newScenePlaceholder } from "./draft";
import {
  ButtonGroup,
  ColorField,
  EmojiField,
  Field,
  TextArea,
  TextInput,
  ToastBanner,
  VarMapEditor,
  type VarValue,
} from "./ui";
import { PreviewPlayer } from "./PreviewPlayer";
import { navigate } from "@/router";

const KIND_LABELS: Record<SceneKind, string> = {
  narrative: "📖 Narrative",
  floor: "🏢 Floor",
  ending: "🏁 Ending",
};

interface EditorDraft {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  coverEmoji: string;
  coverColor: string;
  schema: "linear" | "roguelike";
  model: StoryModel;
  published: boolean;
}

function freshDraft(): EditorDraft {
  return {
    id: "",
    title: "My Tech Adventure",
    subtitle: "A short coding tale",
    description: "",
    coverEmoji: "🚀",
    coverColor: "#1a1a2e",
    schema: "roguelike",
    model: starterModel("roguelike"),
    published: false,
  };
}

export function StoryEditor({ id }: { id: string }) {
  const [draft, setDraft] = useState<EditorDraft | null>(
    id ? null : freshDraft()
  );
  const [cast, setCast] = useState<string[]>([]);
  const [library, setLibrary] = useState<CharacterRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [showInk, setShowInk] = useState(false);
  const [banner, setBanner] = useState<{
    kind: "error" | "ok" | "warn";
    message: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [charsMine, charsPublic] = await Promise.all([
        listCharacters(true),
        listCharacters(false),
      ]);
      if (cancelled) return;
      const seen = new Set<string>();
      const merged: CharacterRow[] = [];
      for (const c of [...charsMine, ...charsPublic]) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          merged.push(c);
        }
      }
      setLibrary(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      const row = await getStory(id);
      const castRows = await getStoryCast(id);
      if (cancelled) return;
      if (!row) {
        setBanner({ kind: "error", message: "Story not found." });
        setDraft(freshDraft());
        return;
      }
      const model =
        row.model?.scenes?.length ||
        Object.keys(row.model?.variables ?? {}).length
          ? row.model
          : starterModel(row.schema);
      setDraft({
        id: row.id,
        title: row.title,
        subtitle: row.subtitle,
        description: row.description,
        coverEmoji: row.cover_emoji,
        coverColor: row.cover_color,
        schema: row.schema,
        model,
        published: row.published,
      });
      setCast(castRows.map((c) => c.id));
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const report: CompileReport | null = useMemo(
    () => (draft ? compileStory(draft.model) : null),
    [draft]
  );

  if (!draft) {
    return <p className="muted">Loading story…</p>;
  }

  const patch = (updates: Partial<EditorDraft>): void => {
    setDraft((d) => (d ? { ...d, ...updates } : d));
  };

  const switchSchema = (schema: "linear" | "roguelike"): void => {
    if (schema === draft.schema) return;
    if (
      !window.confirm(
        `Switching to a ${schema} story replaces the outline with a fresh ${schema} template. Continue?`
      )
    ) {
      return;
    }
    patch({ schema, model: starterModel(schema) });
  };

  const patchModel = (model: StoryModel): void => patch({ model });

  const updateScene = (sceneId: string, updates: Partial<StoryScene>): void => {
    patchModel({
      ...draft.model,
      scenes: draft.model.scenes.map((s) =>
        s.id === sceneId ? { ...s, ...updates } : s
      ),
    });
  };

  const addScene = (kind: SceneKind): void => {
    const taken = new Set(draft.model.scenes.map((s) => s.id));
    const idOffset = String(draft.model.scenes.length + 1);
    const scene = newScenePlaceholder(kind, idOffset);
    if (taken.has(scene.id)) scene.id = `scene_${Date.now().toString(36)}`;
    patchModel({ ...draft.model, scenes: [...draft.model.scenes, scene] });
  };

  const removeScene = (sceneId: string): void => {
    if (!window.confirm("Delete this scene and all of its choices?")) return;
    patchModel({
      ...draft.model,
      scenes: draft.model.scenes
        .filter((s) => s.id !== sceneId)
        .map((s) => ({
          ...s,
          choices: s.choices.map((c) => ({
            ...c,
            target: c.target === sceneId ? null : c.target,
          })),
        })),
    });
  };

  const updateChoice = (
    sceneId: string,
    choiceId: string,
    updates: Partial<SceneChoice>
  ): void => {
    patchModel({
      ...draft.model,
      scenes: draft.model.scenes.map((s) =>
        s.id === sceneId
          ? {
              ...s,
              choices: s.choices.map((c) =>
                c.id === choiceId ? { ...c, ...updates } : c
              ),
            }
          : s
      ),
    });
  };

  const addChoice = (sceneId: string): void => {
    patchModel({
      ...draft.model,
      scenes: draft.model.scenes.map((s) =>
        s.id === sceneId
          ? {
              ...s,
              choices: [
                ...s.choices,
                {
                  id: newChoiceId(),
                  label: "",
                  condition: "",
                  set: {},
                  add: {},
                  lines: [],
                  tags: [],
                  target: null,
                } satisfies SceneChoice,
              ],
            }
          : s
      ),
    });
  };

  const removeChoice = (sceneId: string, choiceId: string): void => {
    patchModel({
      ...draft.model,
      scenes: draft.model.scenes.map((s) =>
        s.id === sceneId
          ? { ...s, choices: s.choices.filter((c) => c.id !== choiceId) }
          : s
      ),
    });
  };

  const save = async (publish?: boolean): Promise<void> => {
    if (!report) return;
    if (report.errors.length > 0) {
      setBanner({
        kind: "error",
        message: `Cannot save: compile errors (${report.errors.join("; ")})`,
      });
      return;
    }
    setSaving(true);
    const targetPublished = publish ?? draft.published;
    const { row, error } = await saveStory({
      id: draft.id || undefined,
      title: draft.title.trim() || "Untitled story",
      subtitle: draft.subtitle,
      description: draft.description,
      coverEmoji: draft.coverEmoji,
      coverColor: draft.coverColor,
      schema: draft.schema,
      model: draft.model,
      inkSource: report.ink,
      inkJson: report.json
        ? (JSON.parse(report.json) as Record<string, unknown>)
        : null,
      published: targetPublished,
    });
    setSaving(false);

    if (error) {
      setBanner({ kind: "error", message: error });
      return;
    }
    if (row) {
      if (!draft.id) navigate(`/edit/story/${row.id}`);
      const castError = await setStoryCast(row.id, cast);
      setBanner({
        kind: castError ? "error" : "ok",
        message:
          castError ?? (targetPublished ? "Saved and published 🚀" : "Saved"),
      });
    }
  };

  const remove = async (): Promise<void> => {
    if (!draft.id) return;
    if (!window.confirm(`Delete story "${draft.title}"?`)) return;
    const error = await deleteStory(draft.id);
    setBanner(error ? { kind: "error", message: error } : null);
    if (!error) navigate("/");
  };

  const togglePublish = async (): Promise<void> => {
    if (!draft.id) {
      await save(true);
      return;
    }
    const error = await setStoryPublished(draft.id, !draft.published);
    if (error) {
      setBanner({ kind: "error", message: error });
      return;
    }
    patch({ published: !draft.published });
    setBanner({
      kind: "ok",
      message: draft.published ? "Unpublished" : "Published 🚀",
    });
  };

  const sceneOptions = draft.model.scenes.map((s) => ({
    id: s.id,
    label: `${KIND_LABELS[s.kind]} · ${s.title || s.id}`,
  }));

  return (
    <div className="editor-page">
      <header className="editor-header">
        <div className="editor-title-block">
          <h3 className="hub-logo">Story studio</h3>
          <input
            className="editor-input story-title-input"
            value={draft.title}
            placeholder="Story title"
            onInput={(e) => patch({ title: e.currentTarget.value })}
          />
        </div>
        <ButtonGroup>
          <button className="choice-button" onClick={() => navigate("/")}>
            ← Hub
          </button>
          <button
            className="choice-button"
            onClick={() => setShowInk((v) => !v)}
          >
            {showInk ? "Hide ink" : "View ink"}
          </button>
          <button
            className="choice-button danger"
            disabled={!draft.id}
            onClick={() => void remove()}
          >
            Delete
          </button>
          <button
            className="choice-button"
            disabled={saving}
            onClick={() => void togglePublish()}
          >
            {draft.published ? "Unpublish" : "Publish"}
          </button>
          <button
            className="choice-button primary"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </ButtonGroup>
      </header>

      <ToastBanner
        kind={banner?.kind ?? "ok"}
        message={banner?.message ?? null}
      />

      <div className="editor-layout">
        <div className="editor-main">
          <section className="panel">
            <h3>Presentation</h3>
            <div className="editor-grid-2col">
              <Field label="Subtitle">
                <TextInput
                  value={draft.subtitle}
                  onChange={(v) => patch({ subtitle: v })}
                />
              </Field>
              <Field label="Be kind">
                <select
                  className="editor-input"
                  value={draft.schema}
                  onChange={(e) =>
                    switchSchema(
                      e.currentTarget.value as "linear" | "roguelike"
                    )
                  }
                >
                  <option value="roguelike">
                    ♾ Roguelike (floors, health, tiers)
                  </option>
                  <option value="linear">📖 Linear (fixed scenes)</option>
                </select>
              </Field>
              <Field label="Description">
                <TextArea
                  value={draft.description}
                  onChange={(v) => patch({ description: v })}
                  rows={2}
                />
              </Field>
              <Field label="Cover colour">
                <ColorField
                  value={draft.coverColor}
                  onChange={(v) => patch({ coverColor: v })}
                />
              </Field>
              <Field label="Cover emoji">
                <EmojiField
                  value={draft.coverEmoji}
                  onChange={(v) => patch({ coverEmoji: v })}
                />
              </Field>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h3>Story state</h3>
              <span className="muted">
                Health, money, floor, flags — anything your scenes can read and
                change.
              </span>
            </div>
            <kbd className="hint-key">{"{health}"}</kbd>
            <VarMapEditor
              value={draft.model.variables}
              onChange={(variables) =>
                patchModel({ ...draft.model, variables })
              }
              placeholder="default value"
            />
          </section>

          <section className="panel">
            <div className="panel-head">
              <h3>Messages</h3>
              <span className="muted">
                Short toasts you can trigger with {"MESSAGE::<key>"} or the tag
                any scene emits.
              </span>
            </div>
            <VarMapEditor
              value={draft.model.messages as Record<string, VarValue>}
              onChange={(messages) =>
                patchModel({
                  ...draft.model,
                  messages: messages as Record<string, string>,
                })
              }
              placeholder="toast text"
            />
          </section>

          <section className="panel">
            <div className="panel-head">
              <h3>Scenes</h3>
              <span className="muted">
                Every scene needs at least one line or choice.
              </span>
            </div>

            <div className="scene-list">
              {draft.model.scenes.map((scene) => (
                <SceneCard
                  key={scene.id}
                  scene={scene}
                  schema={draft.schema}
                  sceneOptions={sceneOptions}
                  isStart={draft.model.start === scene.id}
                  onChange={(updates) => updateScene(scene.id, updates)}
                  onRemove={() => removeScene(scene.id)}
                  onAddChoice={() => addChoice(scene.id)}
                  onUpdateChoice={(choiceId, updates) =>
                    updateChoice(scene.id, choiceId, updates)
                  }
                  onRemoveChoice={(choiceId) =>
                    removeChoice(scene.id, choiceId)
                  }
                />
              ))}
            </div>

            <div className="button-row">
              <button
                className="choice-button"
                onClick={() => addScene("narrative")}
              >
                + Narrative scene
              </button>
              {draft.schema === "roguelike" ? (
                <button
                  className="choice-button"
                  onClick={() => addScene("floor")}
                >
                  + Floor
                </button>
              ) : null}
              <button
                className="choice-button"
                onClick={() => addScene("ending")}
              >
                + Ending
              </button>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h3>Cast</h3>
              <span className="muted">
                Attach reusable characters (stored in your library) to this
                story.
              </span>
            </div>
            {library.length === 0 ? (
              <p className="muted">
                No characters in your library yet. Create some in the character
                studio.
              </p>
            ) : (
              <div className="cast-grid">
                {library.map((c) => (
                  <label className="cast-item" key={c.id}>
                    <input
                      type="checkbox"
                      checked={cast.includes(c.id)}
                      onChange={(e) =>
                        setCast((prev) =>
                          e.currentTarget.checked
                            ? [...prev, c.id]
                            : prev.filter((x) => x !== c.id)
                        )
                      }
                    />
                    <span className="avatar-emoji small">
                      {c.avatar_emoji ?? "🧑‍💻"}
                    </span>
                    <span className="cast-name">
                      {c.title || c.role || c.id}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="editor-side">
          <PreviewPanel report={report} showInk={showInk} draft={draft} />
        </aside>
      </div>
    </div>
  );
}

function SceneCard({
  scene,
  schema,
  sceneOptions,
  isStart,
  onChange,
  onRemove,
  onAddChoice,
  onUpdateChoice,
  onRemoveChoice,
}: {
  scene: StoryScene;
  schema: "linear" | "roguelike";
  sceneOptions: { id: string; label: string }[];
  isStart: boolean;
  onChange: (updates: Partial<StoryScene>) => void;
  onRemove: () => void;
  onAddChoice: () => void;
  onUpdateChoice: (choiceId: string, updates: Partial<SceneChoice>) => void;
  onRemoveChoice: (choiceId: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  const setLines = (lines: string[]): void => onChange({ lines });

  return (
    <div className={`scene-card${isStart ? " is-start" : ""}`}>
      <div className="scene-header">
        <button
          type="button"
          className="scene-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="scene-icon">{open ? "▼" : "▶"}</span>
          <span className="scene-kind">{KIND_LABELS[scene.kind]}</span>
          <input
            className="editor-input scene-title-input"
            value={scene.title}
            placeholder="Scene title"
            onInput={(e) => onChange({ title: e.currentTarget.value })}
            onClick={(e) => e.stopPropagation()}
          />
          {isStart ? <span className="badge badge-start">start</span> : null}
        </button>
        <div className="scene-actions">
          <span className="scene-id">{scene.id}</span>
          <button className="icon-btn" title="Delete scene" onClick={onRemove}>
            ×
          </button>
        </div>
      </div>

      {open ? (
        <div className="scene-body">
          <div className="editor-grid-2col">
            <Field label="Key" hint="Used by SCENE:: tag for colours.">
              <TextInput
                value={scene.sceneKey}
                onChange={(v) => onChange({ sceneKey: v })}
              />
            </Field>
            {scene.kind === "floor" && schema === "roguelike" ? (
              <Field label="Tier">
                <select
                  className="editor-input"
                  value={scene.tier}
                  onChange={(e) =>
                    onChange({
                      tier: Number(e.currentTarget.value) as 1 | 2 | 3,
                    })
                  }
                >
                  <option value={1}>Tier 1 (depth 1–3)</option>
                  <option value={2}>Tier 2 (depth 4–6)</option>
                  <option value={3}>Tier 3 (boss)</option>
                </select>
              </Field>
            ) : null}
            <Field label="Scene colour">
              <ColorField
                value={scene.color}
                onChange={(v) => onChange({ color: v })}
              />
            </Field>
            <Field label="Tags (one per line)">
              <TextArea
                value={scene.tags.join("\n")}
                onChange={(v) =>
                  onChange({
                    tags: v
                      .split("\n")
                      .map((t) => t.trim())
                      .filter(Boolean),
                  })
                }
                rows={2}
              />
            </Field>
          </div>

          <div className="line-editor">
            <span className="field-label">
              Story text (each line = new paragraph)
            </span>
            {scene.lines.map((line, i) => (
              <div className="row-pair" key={i}>
                <TextInput
                  value={line}
                  onChange={(v) => {
                    const next = [...scene.lines];
                    next[i] = v;
                    setLines(next);
                  }}
                  placeholder="A paragraph…"
                />
                <button
                  className="icon-btn"
                  title="Remove line"
                  onClick={() =>
                    setLines(scene.lines.filter((_, j) => j !== i))
                  }
                >
                  ×
                </button>
              </div>
            ))}
            <button
              className="choice-button"
              onClick={() => setLines([...scene.lines, ""])}
            >
              + Text
            </button>
          </div>

          <div className="choices-block">
            <span className="field-label">
              Player choices{" "}
              <span className="muted">
                (target a scene to continue, or “end story” to stop)
              </span>
            </span>
            {scene.choices.map((choice) => (
              <ChoiceCard
                key={choice.id}
                choice={choice}
                sceneOptions={sceneOptions}
                onChange={(updates) => onUpdateChoice(choice.id, updates)}
                onRemove={() => onRemoveChoice(choice.id)}
              />
            ))}
            <button className="choice-button" onClick={onAddChoice}>
              + Choice
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ChoiceCard({
  choice,
  sceneOptions,
  onChange,
  onRemove,
}: {
  choice: SceneChoice;
  sceneOptions: { id: string; label: string }[];
  onChange: (updates: Partial<SceneChoice>) => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div className="choice-card">
      <div className="choice-head">
        <TextInput
          value={choice.label}
          placeholder="Choice label"
          onChange={(v) => onChange({ label: v })}
        />
        <button className="icon-btn" title="Remove choice" onClick={onRemove}>
          ×
        </button>
      </div>

      <div className="editor-grid-2col">
        <Field
          label="Condition (optional)"
          hint="Shown only when true, e.g. health > 30."
        >
          <TextArea
            value={choice.condition}
            onChange={(v) => onChange({ condition: v })}
            rows={1}
          />
        </Field>
        <Field label="Goes to">
          <select
            className="editor-input"
            value={choice.target ?? ""}
            onChange={(e) =>
              onChange({ target: e.currentTarget.value || null })
            }
          >
            <option value="">(end the story)</option>
            {sceneOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="effect-cols">
        <Field label="Set (exact values)" hint="~ turns into = ">
          <VarMapEditor
            value={choice.set}
            onChange={(v) => onChange({ set: v })}
            placeholder="value"
          />
        </Field>
        <Field label="Add (delta values)" hint="negative numbers subtract">
          <VarMapEditor
            value={choice.add}
            onChange={(v) => onChange({ add: v })}
            placeholder="delta"
          />
        </Field>
      </div>

      <Field label="Choice text (lines shown when picked)">
        <div className="line-editor">
          {choice.lines.map((line, i) => (
            <div className="row-pair" key={i}>
              <TextInput
                value={line}
                onChange={(v) => {
                  const next = [...choice.lines];
                  next[i] = v;
                  onChange({ lines: next });
                }}
                placeholder="Paragraph…"
              />
              <button
                className="icon-btn"
                title="Remove line"
                onClick={() =>
                  onChange({ lines: choice.lines.filter((_, j) => j !== i) })
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            className="choice-button"
            onClick={() => onChange({ lines: [...choice.lines, ""] })}
          >
            + Text
          </button>
        </div>
      </Field>

      <Field label="Choice tags (one per line, e.g. MESSAGE::danger)">
        <TextArea
          value={choice.tags.join("\n")}
          onChange={(v) =>
            onChange({
              tags: v
                .split("\n")
                .map((t) => t.trim())
                .filter(Boolean),
            })
          }
          rows={1}
        />
      </Field>
    </div>
  );
}

function PreviewPanel({
  report,
  showInk,
  draft,
}: {
  report: CompileReport | null;
  showInk: boolean;
  draft: EditorDraft;
}): JSX.Element {
  if (!report) return <div className="preview empty">Building preview…</div>;

  if (showInk) {
    return (
      <div className="panel">
        <div className="panel-head">
          <h3>Generated ink</h3>
          <span className="muted">{draft.schema}</span>
        </div>
        <pre className="ink-raw">{report.ink}</pre>
      </div>
    );
  }

  return (
    <div className="panel">
      <PreviewPlayer report={report} />
      {report.errors.length > 0 ? (
        <div className="compile-issues error">
          <strong>Errors</strong>
          {report.errors.map((e, i) => (
            <p key={i}>{e}</p>
          ))}
        </div>
      ) : report.warnings.length > 0 ? (
        <div className="compile-issues warn">
          <strong>Warnings</strong>
          {report.warnings.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
        </div>
      ) : (
        <div className="compile-issues ok">
          <strong>Compiles cleanly</strong>
        </div>
      )}
    </div>
  );
}
